import { AutodocType } from '@sonar-software/sonar-core';
import { EditorRef } from '@ui/Editor';
import { PlaintextEditorProps } from './PlaintextEditor';
import { SuggestionField } from './SuggestionField';
import { TextEditor } from '@periscopes/general/editor';
import { UseSuggestionEditorResult } from '@hooks';
import React, { useCallback, useRef, useState } from 'react';

export type SuggestTextEditorProps = PlaintextEditorProps & {
	autodocType: AutodocType;
	onSave: (value: string) => void;
	sonarObjectId: number;
	suggestion: UseSuggestionEditorResult;
};

export const SuggestTextEditor: React.FC<SuggestTextEditorProps> = ({
	autodocType,
	isReadOnly,
	onSave,
	placeholder,
	sonarObjectId,
	suggestion,
	value,
	...rest
}) => {
	const editorRef = useRef<EditorRef>(null);
	const [ hasContent, setHasContent ] = useState(false);

	const onEdit = useCallback(() => {
		suggestion.accept();
		editorRef.current?.edit();
	}, [ suggestion ]);

	const onAccept = useCallback(() => {
		suggestion.accept();
		editorRef.current?.save();
	}, [ suggestion ]);

	const onFocus = useCallback(() => {
		suggestion.accept();
	}, [ suggestion ]);

	const onBlur = useCallback(() => {
		const userInput = !suggestion.value && hasContent;
		if (!(suggestion.value && suggestion.accepted) && !userInput) {
			suggestion.reset();
		}
	}, [ hasContent, suggestion ]);

	const onCancel = useCallback(() => {
		suggestion.reset();
	}, [ suggestion ]);

	const handleContentCheck = useCallback((hasEditorContent: boolean) => {
		setHasContent(hasEditorContent);
	}, []);

	return (
		<SuggestionField
			autodocType={ autodocType }
			isReadOnly={ isReadOnly }
			onAccept={ onAccept }
			onBlur={ onBlur }
			onEdit={ onEdit }
			placeholder={ placeholder }
			suggestion={ suggestion }>
			<TextEditor
				{ ...rest }
				canBeEmpty
				content={ suggestion.editorValue }
				contentCheck={ handleContentCheck }
				isReadOnly={ isReadOnly }
				onBlur={ onBlur }
				onCancel={ onCancel }
				onFocus={ onFocus }
				onSave={ onSave }
				placeholder={ placeholder }
				ref={ editorRef }
				saveOnBlur={ false } />
		</SuggestionField>
	);
};
